import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, input, signal, TemplateRef, ViewChild } from '@angular/core';
import { GenAiApi } from 'api/gen-ai/gen-ai-api';
import { GenAiDb } from 'lib/cosmos/gen-ai/genAiDb';
import { UserDb } from 'lib/cosmos/user/userDb';
import { stringHelper } from 'lib/helpers/string-helper.class';
import { AppLogService } from 'lib/services/app-log.service';
import { map } from 'rxjs';
import { CoreComponent } from '../core/core.component';
import { DialogService, SelectionDialogItem } from '../dialog';
import { FormLogHandler, FormSaveHandler } from '../form/form-save-button.component';
import { ListItem } from '../list';

@Component({
	selector: 'lib-gen-ai-chat',
	templateUrl: './gen-ai-chat.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class GenAiChatComponent extends CoreComponent {
	@ViewChild('viewPromptTemplate') readonly viewPromptTemplate?: TemplateRef<any>;

	public readonly clientContext = input<UserDb.IUserItem | undefined>(undefined);
	public readonly isLoading = signal<boolean>(false);
	public readonly promptResponse = signal<GenAiApi.ResolvePromptResponse | undefined>(undefined);
	public readonly previousPrompts = signal<readonly GenAiDb.IGenAiItem[] | undefined>(undefined);
	public readonly selectedPrompt = signal<GenAiDb.IGenAiItem | undefined>(undefined);
	public readonly availableClientContexts = signal<readonly UserDb.IUserItem[] | undefined>(undefined);

	public readonly selectedClientContext = signal<UserDb.IUserItem | undefined>(undefined);

	public readonly clientContextToUse = computed<UserDb.IUserItem | undefined>(() => {
		const clientContext = this.clientContext();
		const selectedClientContext = this.selectedClientContext();

		return clientContext ?? selectedClientContext;
	});

	public readonly showEditClientContext = computed<boolean>(() => {
		return this.clientContext() ? false : true;
	});

	public readonly previousPromptListItems = computed<readonly ListItem<GenAiDb.IGenAiItem>[]>(() => {
		return (this.previousPrompts() ?? []).map<ListItem<GenAiDb.IGenAiItem>>((prompt) => {
			const promptText = stringHelper.stripHtmlTags(prompt.prompt);
			const generatedText = stringHelper.stripHtmlTags(prompt.generatedText);

			return {
				id: prompt.id,
				title: promptText,
				subtitle: generatedText,
				select: () => {
					this.selectedPrompt.set(prompt);

					this.dialogService.openDialog({
						title: 'View prompt',
						size: 'large',
						template: () => this.viewPromptTemplate
					});
				},
				data: prompt
			};
		});
	});

	public readonly form = {
		prompt: signal<string | undefined>(undefined)
	};

	public readonly promptSubtitle = computed<string>(() => {
		const promptResponse = this.promptResponse();
		return promptResponse?.promptsLeft
			? `You have ${promptResponse.promptsLeft} prompts left. Prompts are reset weekly.`
			: '';
	});

	protected readonly formLogHandler = signal<FormLogHandler>((data) =>
		this.logService.logError({
			email: this.authUser()?.email,
			message: data.message,
			stacktrace: data.stacktrace,
			behavior: undefined
		})
	);

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const prompt = this.form.prompt();

		if (!prompt) {
			return undefined;
		}

		return this.httpService
			.post<GenAiApi.ResolvePromptResponse, GenAiApi.ResolvePromptRequest>((builder) =>
				builder
					.withCustomApiRoute('genai', 'resolvePrompt')
					.withData({
						prompt: prompt,
						contextClientId: this.clientContextToUse()?.id
					})
					.build()
			)
			.pipe(
				map((response) => {
					this.promptResponse.set(response.data);
				})
			);
	});

	constructor(
		private readonly logService: AppLogService,
		private readonly dialogService: DialogService,
		private readonly clipboard: Clipboard
	) {
		super();
	}

	ngOnInit(): void {
		this.initPreviousPrompts();
		this.initAvailableClients();
	}

	handleEditPrompt(): void {
		this.promptResponse.set(undefined);
	}

	handleCopyText(): void {
		const promptResponse = this.promptResponse();
		if (!promptResponse) {
			return;
		}

		this.clipboard.copy(promptResponse.item.generatedText);

		super.notify('Text copied');
	}

	handleCopySelectedPrompt(): void {
		const promptResponse = this.selectedPrompt();
		if (!promptResponse) {
			return;
		}

		this.clipboard.copy(promptResponse.prompt);

		super.notify('Text copied');
	}

	handleCopySelectedResponse(): void {
		const promptResponse = this.selectedPrompt();
		if (!promptResponse) {
			return;
		}

		this.clipboard.copy(promptResponse.generatedText);

		super.notify('Text copied');
	}

	openClientContextSelector(): void {
		const availableClients = this.availableClientContexts();
		if (!availableClients) {
			return;
		}

		this.dialogService.openSelectionDialog({
			title: 'Select client',
			items: availableClients.map<SelectionDialogItem<UserDb.IUserItem>>((client) => ({
				id: client.id,
				title: client.fullName,
				avatar: client.avatar,
				data: client
			})),
			select: (item) => {
				this.selectedClientContext.set(item);
			}
		});
	}

	private initPreviousPrompts(): void {
		super.subscribeToObservable(
			this.httpService.listCustom<GenAiDb.IGenAiItem>('genai', 'getLatestPrompts').pipe(
				map((response) => {
					this.previousPrompts.set(response.data);
				})
			)
		);
	}

	private initAvailableClients(): void {
		super.subscribeToObservable(
			this.httpService.listOwned<UserDb.IUserItem>('user').pipe(
				map((response) => {
					this.availableClientContexts.set(response.data);
				})
			)
		);
	}
}
